import { OnInit, Component, Input, ViewChild } from '@angular/core';
import { Message } from '../../classes/Message';
import { IJobScreen } from '../../interfaces/job/job';
import { IPageResult } from '../../interfaces/page-result';
import { ISupplierFeedbackFilter } from '../../interfaces/supplier-feedback/supplier-feedback-filter';
import { Observable, Subject } from 'rxjs';
import { SupplierFeedbackService } from '../../services/supplier-feedback.service';
import { flatten } from '../../utils/flatten';
import { ISupplierFeedback } from '../../interfaces/supplier-feedback/supplier-feedback';
import { LazyLoadEvent, MessageService, OrderList } from 'primeng';
import { Router } from '@angular/router';
import { NewSupplierFeedbackComponent } from '../supplier-feedback/new-supplier-feedback.component';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'job-screen-supplier-feedback',
  styleUrls: ['./job-screen-supplier-feedback.component.css'],
  templateUrl: './job-screen-supplier-feedback.component.html',
})
export class JobScreenSupplierFeedbackComponent implements OnInit {
  public isBusy: boolean = false;

  @ViewChild('newFeedbackControl') newFeedbackControl: NewSupplierFeedbackComponent;

  private _jobId: number;
  @Input('jobId')
  set jobId(value: number) {
    this._jobId = value;
  }

  get jobId(): number {
    return this._jobId;
  }

  private _jobInfo: IJobScreen = null;
  @Input('jobInfo')
  set jobInfo(value: IJobScreen) {
    this._jobInfo = value;
  }

  get jobInfo(): IJobScreen {
    return this._jobInfo;
  }

  private _orderId: number;
  @Input('orderId')
  set orderId(value: number) {
    this._orderId = value;
  }

  get orderId(): number {
    return this._orderId;
  }

  private _taskId: number;
  @Input('taskId')
  set taskId(value: number) {
    this._taskId = value;
  }

  get taskId(): number {
    return this._taskId;
  }

  private supplierFeedbackFilter: Subject<ISupplierFeedbackFilter>;
  private filterVal: ISupplierFeedbackFilter;
  public supplierFeedbackPage: IPageResult<ISupplierFeedback> = {
    items: [],
    total: 0,
  };

  public isLoadedSupplierFeedback: boolean = false;
  public selectedSupplierFeedback = null;

  constructor(
    public messageService: MessageService,
    private supplierFeedbackService: SupplierFeedbackService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initDefaultFilter();
    this.supplierFeedbackFilter = new Subject<ISupplierFeedbackFilter>();

    // subscribe to filter changes with a debounce time
    this.supplierFeedbackFilter.pipe(debounceTime(500)).subscribe((f) => {
      this.loadJobFeedback(f);
      this.filterVal = f;
    });

    // set default filter
    this.supplierFeedbackFilter.next(this.filterVal);
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'JobNumber',
      sortOrder: 1,
      filters: {
        jobNumber: { matchMode: 'contains' },
        status: { matchMode: 'contains' },
        customerIdList: { value: null },
        supplierIdList: { value: null },
      },
    };
  }

  loadJobFeedback(filter: ISupplierFeedbackFilter) {
    this.isBusy = true;

    // todo: load supplier feedback
    if (this.jobId && this.jobId != 0) {
      //console.log("Loading feedback data for job: ", this.jobId, " - with filter: ", filter);
      this.supplierFeedbackService
        .getPageForJob(this.jobId, flatten(filter))
        .then((result) => {
          this.supplierFeedbackPage.items = result.items;
          this.supplierFeedbackPage.total = result.total;
          this.isLoadedSupplierFeedback = true;
          this.isBusy = false;
        })
        .catch((x) => {
          this.isBusy = false;
          this.messageService.add(
            Message.Error('Error loading supplier feedback.', 'Load Supplier Feedback'),
          );
        });
    } else {
      this.isBusy = false;
    }
  }

  onSupplierFeedbackGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.supplierFeedbackFilter.next(this.filterVal);
  }

  editSupplierFeedback(supplierFeedbackId) {
    var orderList = [];
    this.jobInfo.orders.forEach((x) => {
      orderList.push({ id: x.id, name: x.orderNumber });
    });

    this.newFeedbackControl.editFeedback(
      this.selectedSupplierFeedback.id,
      this.jobInfo,
      orderList,
      this.orderId,
    );
  }

  openSupplierFeedback(supplierFeedbackId) {
    //console.log("navigating to SupplierFeedback: ", this.selectedSupplierFeedback, " - id: ", this.selectedSupplierFeedback.id);
    this.router.navigate(['app/supplierFeedback/' + supplierFeedbackId], {
      queryParams: { backTo: this.router.url },
    });
  }

  onFeedbackCreated() {
    this.initDefaultFilter();
    this.supplierFeedbackFilter.next(this.filterVal);
  }

  addSupplierFeedback() {
    if (this.jobInfo.supplierId == null) return;

    var orderList = [];
    this.jobInfo.orders.forEach((x) => {
      orderList.push({ id: x.id, name: x.orderNumber });
    });

    this.newFeedbackControl.createNewFeedback(this.jobInfo, orderList, this.orderId, this.taskId);
  }
}
